import { doc, onSnapshot } from "firebase/firestore";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../services/firebase";

// Crear el contexto
const AccessibilityContext = createContext();

// Hook personalizado para usar el contexto
export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error(
      "useAccessibility debe usarse dentro de AccessibilityProvider",
    );
  }
  return context;
};

// Valores predeterminados
const defaultSettings = {
  fontSize: "medium", // 'small', 'medium', 'large'
  highContrast: false,
  animations: true,
  textToSpeech: false,
  readingGuide: false,
  lineSpacing: "normal", // 'normal', 'wide', 'wider'
};

// Provider del contexto
export const AccessibilityProvider = ({ children }) => {
  // Intentar cargar configuración guardada o usar valores predeterminados
  const [settings, setSettings] = useState(() => {
    try {
      const savedSettings = localStorage.getItem("accessibility_settings");
      return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
    } catch (error) {
      console.error("Error loading accessibility settings:", error);
      return defaultSettings;
    }
  });

  // AÑADIR: Detectar preferencia del sistema para reducir movimiento
  useEffect(() => {
    // Detectar preferencia de reducción de movimiento del sistema
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    // Función para actualizar configuración basada en preferencia del sistema
    const updateMotionPreference = (event) => {
      if (event.matches) {
        // El usuario prefiere reducir movimiento
        updateSettings({ animations: false });
      }
    };

    // Verificar preferencia inicial
    if (prefersReducedMotion.matches) {
      updateSettings({ animations: false });
    }

    // Agregar listener para cambios en la preferencia
    prefersReducedMotion.addEventListener("change", updateMotionPreference);

    // Limpiar listener
    return () => {
      prefersReducedMotion.removeEventListener(
        "change",
        updateMotionPreference,
      );
    };
  }, []);

  // Guardar configuración en localStorage y aplicar cambios al DOM
  useEffect(() => {
    try {
      localStorage.setItem("accessibility_settings", JSON.stringify(settings));

      document.documentElement.style.setProperty(
        "--font-size-multiplier",
        settings.fontSize === "large"
          ? "1.2"
          : settings.fontSize === "small"
            ? "0.9"
            : "1",
      );

      document.documentElement.style.setProperty(
        "--line-height-multiplier",
        settings.lineSpacing === "wider"
          ? "1.8"
          : settings.lineSpacing === "wide"
            ? "1.5"
            : "1.3",
      );

      // Aplicar clase de alto contraste al body
      if (settings.highContrast) {
        document.body.classList.add("high-contrast");
      } else {
        document.body.classList.remove("high-contrast");
      }

      // Aplicar/Quitar clase y estilos para reducir movimiento
      if (!settings.animations) {
        document.body.classList.add("reduce-motion");
        let styleElement = document.getElementById("reduce-motion-style");
        if (!styleElement) {
          styleElement = document.createElement("style");
          styleElement.id = "reduce-motion-style";
          document.head.appendChild(styleElement);
        }
        // INICIO: Comentario añadido para explicar el CSS complejo
        /*
         * CSS Inyectado para Forzar la Desactivación de Animaciones:
         * - La clase .reduce-motion y las reglas generales (*) desactivan transiciones/animaciones estándar.
         * - Las reglas específicas [data-framer-motion-*] son necesarias para anular
         *   las animaciones controladas directamente por Framer Motion, previniendo
         *   errores o comportamientos inesperados al intentar animar a/desde valores inválidos
         *   cuando las animaciones deben estar desactivadas.
         * - Forzar una duración/retraso mínimos (0.001ms) en lugar de 'none' o 0 a veces
         *   evita glitches en algunos navegadores o librerías.
         */
        // FIN: Comentario añadido
        styleElement.textContent = `
          /* Desactivar animaciones estándar */
          .reduce-motion * {
            transition: none !important;
            animation: none !important;
          }

          /* Overrides específicos para Framer Motion y prevención de errores */
          @media (prefers-reduced-motion: reduce), (prefers-reduced-motion: no-preference) {
            *[data-framer-motion-initial],
            *[data-framer-motion-animate],
            *[data-framer-motion-whilehover],
            *[data-framer-motion-whiletap],
            *[data-framer-motion-exit] {
              /* Forzar valores no animados */
              --scale: 1 !important;
              --x: 0px !important;
              --y: 0px !important;
              --rotate: 0deg !important;
              --rotateX: 0deg !important;
              --rotateY: 0deg !important;
              --rotateZ: 0deg !important;
              --opacity: 1 !important;
              transform: none !important;
              animation: none !important;
              transition: none !important;
            }

            /* Forzar duración mínima para evitar glitches */
            * {
              animation-duration: 0.001ms !important;
              animation-delay: 0.001ms !important;
              transition-duration: 0.001ms !important;
              transition-delay: 0.001ms !important;
            }
          }
        `;
      } else {
        document.body.classList.remove("reduce-motion");
        const styleElement = document.getElementById("reduce-motion-style");
        if (styleElement) {
          styleElement.remove();
        }
      }
    } catch (error) {
      console.error(
        "Error saving accessibility settings/applying DOM changes:",
        error,
      );
    }
  }, [settings]);

  const { currentUser } = useAuth();

  // Sincronización con Firestore
  useEffect(() => {
    if (!currentUser?.uid) return;

    const userDocRef = doc(db, "users", currentUser.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (userDoc) => {
        // Callback de éxito
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accessibility) {
            const newSettings = {};
            // Mapeo de campos entre sistemas
            if (userData.accessibility.fontSize)
              newSettings.fontSize = userData.accessibility.fontSize;

            if (userData.accessibility.highContrast !== undefined)
              newSettings.highContrast = userData.accessibility.highContrast;

            if (userData.accessibility.reducedMotion !== undefined)
              newSettings.animations = !userData.accessibility.reducedMotion;

            // Actualizar el estado local si hay cambios
            if (Object.keys(newSettings).length > 0) {
              console.log(
                "Syncing accessibility settings from Firestore:",
                newSettings,
              );
              updateSettings(newSettings);
            }
          }
        } else {
          console.warn(
            `User document ${currentUser.uid} does not exist for accessibility sync.`,
          );
        }
      },
      (error) => {
        // INICIO: Callback de error añadido
        console.error(
          "Error listening to accessibility settings in Firestore:",
          error,
        );
        // Podrías añadir aquí lógica adicional, como mostrar un mensaje al usuario
      }, // FIN: Callback de error añadido
    );

    return () => unsubscribe();
  }, [currentUser]); // Asegúrate de que updateSettings no esté aquí si no es necesario

  // Métodos para actualizar configuración
  const updateSettings = (newSettings) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  };

  const toggleFontSize = () => {
    const sizes = ["small", "medium", "large"];
    const currentIndex = sizes.indexOf(settings.fontSize);
    const nextIndex = (currentIndex + 1) % sizes.length;
    updateSettings({ fontSize: sizes[nextIndex] });
  };

  const toggleHighContrast = () => {
    updateSettings({ highContrast: !settings.highContrast });
  };

  const toggleAnimations = () => {
    updateSettings({ animations: !settings.animations });
  };

  const toggleTextToSpeech = () => {
    updateSettings({ textToSpeech: !settings.textToSpeech });
  };

  const toggleReadingGuide = () => {
    updateSettings({ readingGuide: !settings.readingGuide });
  };

  const setLineSpacing = (spacing) => {
    updateSettings({ lineSpacing: spacing });
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const contextValue = {
    ...settings,
    updateSettings,
    toggleFontSize,
    toggleHighContrast,
    toggleAnimations,
    toggleTextToSpeech,
    toggleReadingGuide,
    setLineSpacing,
    resetSettings,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityContext;
